import type { CabinetTenantMembership } from "@planerius/contracts/cabinet-auth"
import { useRouter } from "@tanstack/react-router"
import { type ReactNode, useEffect, useMemo, useState } from "react"

import { accessStore } from "../../access/access-store"
import { useCabinetAccess } from "../../access/use-access"
import { listTenants, selectTenant } from "../../api/auth.api"
import { fetchEmployees } from "../../api/directory.api"
import { isApiProblem } from "../../api/problem"
import { countOf } from "../../lib/plural"
import { useNavBadges } from "../../shell/nav-badges"
import { Button } from "../../ui"
import { useEventsBadge } from "../notifications/use-events-badge"
import { useResource } from "../directories/use-resource"
import { initials } from "../profile/profile-model"
import styles from "../profile/profile.module.css"
import { PasswordBlock, SessionsBlock, SignOutBlock } from "../profile/security-blocks"

/**
 * 5-М7 «Профиль мастера» — /my/profile.
 *
 * ЗАЧЕМ ЭКРАН ВООБЩЕ. До него мастеру нечем было выйти из кабинета: ни кнопки, ни пункта.
 * А телефон мастера — самое частое «чужое устройство» салона: его теряют, меняют, отдают
 * родственникам. Поэтому здесь, кроме выхода, «завершить все, кроме этого».
 *
 * «ЧТО МНЕ ПРИХОДИТ» — ЧЕСТНЫЙ ТЕКСТ, А НЕ ЛИСТОВОЙ. Макет пишет «уведомления приходят
 * в бот», но персоналу сейчас не доставляется ничего: ни бота, ни пушей. Обещание «ничего
 * настраивать не нужно» оставило бы мастера без сведений об отменах — так что экран
 * говорит, где смотреть записи на самом деле.
 */
export function MasterProfileScreen(): ReactNode {
  const { context } = useCabinetAccess()
  const router = useRouter()
  const [reloadKey, setReloadKey] = useState(0)
  const [salons, setSalons] = useState<readonly CabinetTenantMembership[]>([])
  const [switchProblem, setSwitchProblem] = useState<string | null>(null)

  /*
   * Счётчик дел — тот же, что у колокольчика в кабинете, и спрашивается так же: оболочки
   * администратора у мастера нет (у него своя, MasterFrame), поэтому спрашивает экран,
   * на котором стоит кнопка в ленту. Филиал не сужаем: мастер работает в своих точках,
   * и дело в любой из них — его дело.
   */
  useEventsBadge(undefined)
  const badges = useNavBadges()

  const employees = useResource((signal) => fetchEmployees(false, signal), "master-profile-employees")
  const me = useMemo(
    () => employees.data?.find((employee) => employee.id === context.employeeId) ?? null,
    [employees.data, context.employeeId],
  )

  useEffect(() => {
    const controller = new AbortController()
    listTenants(controller.signal)
      .then((list) => {
        setSalons(list.filter((item) => item.memberStatus === "active" && item.tenantStatus !== "archived"))
        return list
      })
      .catch(() => {
        // Список салонов — удобство, а не условие экрана: без него просто нет переключателя.
        setSalons([])
      })
    return () => controller.abort()
  }, [])

  const switchSalon = async (tenantId: string): Promise<void> => {
    if (tenantId === context.tenant.id) return
    setSwitchProblem(null)
    try {
      await selectTenant(tenantId)
      await accessStore.reload()
      await router.navigate({ to: "/my" })
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setSwitchProblem(error.detail ?? error.title)
    }
  }

  const branches = context.branches.map((branch) => branch.name).join(", ")

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <span className={styles.avatar} aria-hidden="true">
          {initials(context.user.displayName)}
        </span>
        <span className={styles.headerMain}>
          <span className={styles.name}>{context.user.displayName}</span>
          <span className={styles.muted}>
            {context.role.name} · {context.tenant.name}
          </span>
        </span>
        {salons.length > 1 ? (
          <select
            aria-label="Салон"
            value={context.tenant.id}
            onChange={(event) => void switchSalon(event.currentTarget.value)}
          >
            {salons.map((salon) => (
              <option key={salon.tenantId} value={salon.tenantId}>
                {salon.tenantName}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      {switchProblem === null ? null : (
        <div className={styles.error} role="alert">
          {switchProblem}
        </div>
      )}

      <section className={styles.block}>
        <span className={styles.blockTitle}>Как меня видит клиент</span>
        <dl className={styles.facts}>
          <dt>Публичное имя</dt>
          <dd>{me?.publicName ?? me?.displayName ?? context.user.displayName}</dd>
          <dt>Фото</dt>
          <dd>{me === null ? "—" : me.avatarUrl === null ? "не загружено" : "загружено"}</dd>
          <dt>Мои услуги</dt>
          <dd>{me === null ? "—" : `${countOf(me.serviceIds.length, "услуга", "услуги", "услуг")} · только просмотр`}</dd>
          <dt>Филиалы</dt>
          <dd>{branches.length === 0 ? "—" : branches}</dd>
        </dl>
        <p className={styles.hint}>Описание на сайте редактирует управляющий.</p>
      </section>

      <section className={styles.block}>
        <span className={styles.blockTitle}>Что мне приходит</span>
        {/*
         * ЛЕНТА ОТКРЫВАЕТСЯ ОТСЮДА, А НЕ ПЯТОЙ ВКЛАДКОЙ. Четыре вкладки — это то, что
         * помещается под палец на 360 px; пятая сделала бы тесно всем четырём. Число дел
         * стоит в самой строке: иначе её нажимают, чтобы проверить, есть ли там что-нибудь.
         */}
        <Button
          variant="secondary"
          onClick={() => {
            void router.navigate({ to: "/my/notifications", search: {} })
          }}
        >
          Уведомления
          {badges.notifications === undefined || badges.notifications === 0
            ? ""
            : ` · требуют действия: ${badges.notifications}`}
        </Button>
        <p className={styles.hint}>
          Отмены, переносы ваших записей и правки графика приходят в ленту уведомлений.
          Внешней доставки — в бот, на почту, пушем — пока нет, поэтому заглядывайте сами.
        </p>
      </section>

      <PasswordBlock
        passwordChangedAt={context.user.passwordChangedAt}
        onChanged={() => {
          void accessStore.refresh()
          setReloadKey((value) => value + 1)
        }}
      />
      <SessionsBlock title="Безопасность" reloadKey={reloadKey} />
      <SignOutBlock />
    </div>
  )
}
